/* eslint-disable @nx/enforce-module-boundaries */
import { ChevronLeftIcon } from '@chakra-ui/icons';
import {
  CloseButton,
  HStack,
  IconButton,
  Image,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@mybridge/ui';
import { forwardRef, useContext, useMemo, useState } from 'react';
import {
  AttendingIconMap,
  CelebratingIconMap,
  FeelingsIconMap,
} from 'v4/lib/constants';
import { PostFormContext } from '../context';
import { toPascalCase } from 'v4/lib/commons';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';

export const ProfileFeelingsPicker = forwardRef(({setFeeling, onClickClose, ...props }, ref) => {
  const dispatch = useDispatch();
  const { feelingsAndActivities } = useSelector((s) => s.globalData) ?? {};
  const { t } = useTranslator();
  
  // Create tabs using the translated category names instead of object keys
  const tabs = useMemo(() => {
    if (!feelingsAndActivities) return [];
    
    return Object.keys(feelingsAndActivities).map(key => {
      const items = feelingsAndActivities[key];
      // Get the category name from the first item in each array (since all items in same array have same category)
      return items.length > 0 ? items[0].category : key;
    });
  }, [feelingsAndActivities]);

  // Create a mapping to get the original key from translated category
  const categoryToKeyMap = useMemo(() => {
    if (!feelingsAndActivities) return {};
    
    const map = {};
    Object.keys(feelingsAndActivities).forEach(key => {
      const items = feelingsAndActivities[key];
      if (items.length > 0) {
        map[items[0].category] = key;
      }
    });
    return map;
  }, [feelingsAndActivities]);

  const [activeTab, setActiveTab] = useState(() => {
    if (tabs.length > 3) return tabs[3];
    return tabs[0] || '';
  });

  const onSelect = (val) => {

    setFeeling(val);
  };

  // Get the current tab index
  const currentTabIndex = tabs.indexOf(activeTab);

  return (
    <>
      <HStack mb={4}>
        <HStack flex={1}>
          <Text fontWeight="400" color="brandPrimary.500">
            {t('profile.whatIsGoingOn') || "What is going on?"}
          </Text>
        </HStack>
        <CloseButton onClick={() => onClickClose(true)} />
      </HStack>
      <Tabs index={currentTabIndex}>
        <TabList overflowX="auto" whiteSpace="nowrap" sx={{ '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
          {tabs?.map?.((translatedCategory, tIndex) => (
            <Tab fontSize="15px" onClick={(e) => setActiveTab(translatedCategory)} key={tIndex}>
              {translatedCategory}
            </Tab>
          ))}
        </TabList>
        <TabPanels maxH={['260px', '350px']} overflowY="auto">
          {tabs?.map?.((translatedCategory, faIndex) => {
            // Get the original key to access the data
            const originalKey = categoryToKeyMap[translatedCategory];
            const items = feelingsAndActivities[originalKey] || [];
            
            return (
              <TabPanel px={0} key={faIndex}>
                {items?.length ? (
                  <HStack
                    flexDir={faIndex < 1 ? 'row' : 'column'}
                    spacing={0}
                    alignItems="flex-start"
                    flexWrap="wrap"
                  >
                    {items?.map?.((f, fIndex) => (
                      <HStack
                        cursor="pointer"
                        _hover={{ bg: 'gray.100' }}
                        p={2}
                        borderRadius="5"
                        key={fIndex}
                        w={faIndex < 1 ? ['50%', '33.33%'] : 'full'}
                        onClick={(e) => onSelect(f)}
                      >
                        <Stack flexShrink={0}>
                          <Image src={f.file} w="6" h="6" />
                        </Stack>
                        <Text wordBreak="none">{f?.name}</Text>
                      </HStack>
                    ))}
                  </HStack>
                ) : (
                  ''
                )}
              </TabPanel>
            );
          })}
        </TabPanels>
      </Tabs>
    </>
  );
});
